import React, { useState, useEffect, useContext, useMemo } from "react";
import { Container, Row, Col, InputGroup, Form } from "react-bootstrap";
import { Plus, Search, Pencil, Trash2, GraduationCap, Users, X, ChevronDown } from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";
import "../../../assets/css/gcadmin/faqmanagement/FAQManagement.css";

const ITEMS_PER_PAGE = 8; // yahan se page size control hoti hai

const EMPTY_FORM = { audience: "Student", question: "", answer: "" };

// ---- shared sweetalert presets so every alert looks consistent ----
const successAlert = (title, text) =>
  Swal.fire({
    icon: "success",
    title,
    text,
    confirmButtonText: "OK",
    confirmButtonColor: "#036280",
    timer: 2000,
    timerProgressBar: true,
  });

const infoAlert = (title, text) =>
  Swal.fire({
    icon: "info",
    title,
    text,
    confirmButtonText: "OK",
    confirmButtonColor: "#036280",
    timer: 1800,
    timerProgressBar: true,
  });

const errorAlert = (title, text) =>
  Swal.fire({
    icon: "error",
    title,
    text,
    confirmButtonText: "OK",
    confirmButtonColor: "#036280",
  });

const warningAlert = (title, text) =>
  Swal.fire({
    icon: "warning",
    title,
    text,
    confirmButtonText: "OK",
    confirmButtonColor: "#036280",
  });

// Pulls the most useful message out of an axios error so failures are
// actually debuggable instead of a generic "Failed to ..." toast.
const getApiErrorMessage = (err, fallback) => {
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.response?.data?.errors) {
    const firstError = Object.values(err.response.data.errors)[0];
    if (Array.isArray(firstError)) return firstError[0];
  }
  if (err?.response?.status) return `${fallback} (status ${err.response.status})`;
  if (err?.request) return `${fallback} — no response from server. Check your connection / API URL.`;
  return fallback;
};

const FAQManagementpage = () => {
  const { baseUrl, authToken,  validateFaqForm, } = useContext(AppContext);

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false); // list loading state
  const [submitting, setSubmitting] = useState(false); // create/update in-flight

  const [activeTab, setActiveTab] = useState("Student");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false); // mounted
  const [modalIn, setModalIn] = useState(false); // animated in
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [activeFaqId, setActiveFaqId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const [pageIn, setPageIn] = useState(false);

  useEffect(() => {
    setTimeout(() => setPageIn(true), 60);
    loadAllFaqs();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  /* ── Load ALL FAQs from DB ── */
  const loadAllFaqs = () => {
    setLoading(true);
    axios.post( `${baseUrl}/admin/faq/getAllFaqs`,{},{ headers: {Authorization: `Bearer ${authToken}` }
    }
)
      .then((res) => {
        setLoading(false);
        if (res.data.status) {
          setFaqs(res.data.data);
        } else {
          errorAlert("Failed to load FAQs", res.data.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error("getAllFaqs error:", err);
        errorAlert("Failed to load FAQs", getApiErrorMessage(err, "Failed to load FAQs"));
      });
  };

  // ---- derived counts + filtered/paginated list ----
  const studentCount = faqs.filter((f) => f.audience === "Student").length;
  const teacherCount = faqs.filter((f) => f.audience === "Teacher").length;

  const filteredFaqs = useMemo(() => {
    return faqs
      .filter((f) => f.audience === activeTab)
      .filter((f) => f.question.toLowerCase().includes(searchTerm.trim().toLowerCase()));
  }, [faqs, activeTab, searchTerm]);

  const paginatedFaqs = filteredFaqs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // ---- modal open/close with enter & exit animation ----
  const openCreateModal = () => {
    setModalMode("create");
    setActiveFaqId(null);
    setForm({ ...EMPTY_FORM, audience: activeTab });
    setShowModal(true);
    setTimeout(() => setModalIn(true), 20);
  };

  const openEditModal = (faq) => {
    setModalMode("edit");
    setActiveFaqId(faq.id);
    setForm({ audience: faq.audience, question: faq.question, answer: faq.answer });
    setShowModal(true);
    setTimeout(() => setModalIn(true), 20);
  };

  const closeModal = ({ silent } = {}) => {
    setModalIn(false);
    setTimeout(() => {
      setShowModal(false);
      setForm(EMPTY_FORM);
      setActiveFaqId(null);
    }, 260);
    if (!silent) infoAlert("Cancelled successfully");
  };

  // ---- form handlers ----
  const handleFormChange = (field) => (e) => {

    setForm((prev) => ({
        ...prev,
        [field]: e.target.value,
    }));

    if (errors[field]) {

        setErrors((prev) => ({
            ...prev,
            [field]: "",
        }));

    }
};

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateFaqForm(form);

if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
}

setErrors({});

    setSubmitting(true);

    if (modalMode === "create") {
      axios
        .post(
          `${baseUrl}/admin/faq/createFaq`,
          {audience: form.audience,question: form.question.trim(),answer: form.answer.trim(),},{ headers: { Authorization: `Bearer ${authToken}` } })
        .then((res) => {
          setSubmitting(false);
          if (res.data.status) {
            setFaqs((prev) => [res.data.data, ...prev]);
            setModalIn(false);
            setTimeout(() => {
              setShowModal(false);
              setForm(EMPTY_FORM);
            }, 260);
            successAlert("Created successfully");
          } else {
            errorAlert("Failed to create FAQ", res.data.message);
          }
        })
        .catch((err) => {
          setSubmitting(false);
          console.error("createFaq error:", err);
          errorAlert("Failed to create FAQ", getApiErrorMessage(err, "Failed to create FAQ"));
        });
    } else {
      axios
        .post(
          `${baseUrl}/admin/faq/updateFaq`,
          {
 id: activeFaqId,audience: form.audience,question: form.question.trim(), answer: form.answer.trim(),}, { headers: { Authorization: `Bearer ${authToken}` } })
        .then((res) => {
          setSubmitting(false);
          if (res.data.status) {
            setFaqs((prev) =>
              prev.map((f) => (f.id === activeFaqId ? res.data.data : f))
            );
            setModalIn(false);
            setTimeout(() => {
              setShowModal(false);
              setForm(EMPTY_FORM);
              setActiveFaqId(null);
            }, 260);
            successAlert("Updated successfully");
          } else {
            errorAlert("Failed to update FAQ", res.data.message);
          }
        })
        .catch((err) => {
          setSubmitting(false);
          console.error("updateFaq error:", err);
          errorAlert("Failed to update FAQ", getApiErrorMessage(err, "Failed to update FAQ"));
        });
    }
  };

  const handleDelete = (faq) => {
    Swal.fire({
      icon: "warning",
      title: "Delete this FAQ?",
      text: faq.question,
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#036280",
      cancelButtonColor: "#8a99a3",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        axios .post( `${baseUrl}/admin/faq/deleteFaq`,{ id: faq.id },{ headers: { Authorization: `Bearer ${authToken}` } }  )
          .then((res) => {
            if (res.data.status) {
              setFaqs((prev) => prev.filter((f) => f.id !== faq.id));
              successAlert("Deleted successfully");
            } else {
              errorAlert("Failed to delete FAQ", res.data.message);
            }
          })
          .catch((err) => {
            console.error("deleteFaq error:", err);
            errorAlert("Failed to delete FAQ", getApiErrorMessage(err, "Failed to delete FAQ"));
          });
      }
    });
  };

  return (
    <div className={`gcStudent-faq-wrapper ${pageIn ? "gcStudent-faq-wrapper--in" : ""}`}>
      <Container fluid className="gcStudent-faq-container">
        {/* ---------------- Hero banner ---------------- */}
        <div className="gcStudent-faq-banner">
          <Row className="align-items-center gy-3">
            <Col xs={12} md={8}>
              <span className="gcStudent-faq-badge">FAQ Management</span>
              <h2 className="gcStudent-faq-title">Manage FAQs</h2>
              <p className="gcStudent-faq-subtitle">Curate answers for students and teachers.</p>
            </Col>
            <Col xs={12} md={4} className="text-md-end">
              <button type="button" className="gcStudent-faq-createBtn" onClick={openCreateModal}>
                <Plus size={18} strokeWidth={2.5} />
                <span>Create New FAQ</span>
              </button>
            </Col>
          </Row>
        </div>

        {/* ---------------- Content card ---------------- */}
        <div className="gcStudent-faq-card">
          <Row className="align-items-center gy-3 gcStudent-faq-toolbar">
            <Col xs={12} md="auto">
              <div className="gcStudent-faq-tabs">
                <button
                  type="button"
                  className={`gcStudent-faq-tab ${activeTab === "Student" ? "gcStudent-faq-tabActive" : ""}`}
                  onClick={() => setActiveTab("Student")}
                >
                  <GraduationCap size={16} />
                  <span>Student</span>
                  <span className="gcStudent-faq-tabCount">{studentCount}</span>
                </button>
                <button
                  type="button"
                  className={`gcStudent-faq-tab ${activeTab === "Teacher" ? "gcStudent-faq-tabActive" : ""}`}
                  onClick={() => setActiveTab("Teacher")}
                >
                  <Users size={16} />
                  <span>Teacher</span>
                  <span className="gcStudent-faq-tabCount">{teacherCount}</span>
                </button>
              </div>
            </Col>
            <Col xs={12} md className="ms-md-auto">
              <InputGroup className="gcStudent-faq-search">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>

          {/* ---------------- FAQ list ---------------- */}
          {loading ? (
            <Loader message="FAQs loading, please wait..." />
          ) : (
            <>
              <div className="gcStudent-faq-list">
                {paginatedFaqs.length === 0 && (
                  <div className="gcStudent-faq-empty">No FAQs found for this search.</div>
                )}
                {paginatedFaqs.map((faq, idx) => (
                  <div
                    className="gcStudent-faq-item"
                    key={faq.id}
                    style={{ animationDelay: `${idx * 60}ms` }}
                  >
                    <div className="gcStudent-faq-itemBody">
                      <h4 className="gcStudent-faq-question">{faq.question}</h4>
                      <p className="gcStudent-faq-answer">{faq.answer}</p>
                      <span className="gcStudent-faq-added">
                        Added {faq.created_at ? faq.created_at.slice(0, 10) : ""}
                      </span>
                    </div>
                    <div className="gcStudent-faq-itemActions">
                      <button
                        type="button"
                        className="gcStudent-faq-iconBtn gcStudent-faq-editBtn"
                        onClick={() => openEditModal(faq)}
                        aria-label="Edit FAQ"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        className="gcStudent-faq-iconBtn gcStudent-faq-deleteBtn"
                        onClick={() => handleDelete(faq)}
                        aria-label="Delete FAQ"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <Pagination
                currentPage={currentPage}
                totalItems={filteredFaqs.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </div>
      </Container>

      {/* ---------------- Create / Edit Modal (custom — matches Add New Book) ---------------- */}
      {showModal && (
        <div
          className={`gcStudent-faq-backdrop ${modalIn ? "gcStudent-faq-backdrop--in" : ""}`}
          onClick={() => closeModal()}
        >
          <div
            className={`gcStudent-faq-modal ${modalIn ? "gcStudent-faq-modal--in" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleSubmit}>
              <div className="gcStudent-faq-modalHeader">
                <h5>{modalMode === "create" ? "New FAQ" : "Edit FAQ"}</h5>
                <button type="button" className="gcStudent-faq-modalClose" onClick={() => closeModal()}>
                  <X size={16} />
                </button>
              </div>

              <div className="gcStudent-faq-modalBody">
                <div className="gcStudent-faq-formGroup">
                  <label className="gcStudent-faq-formLabel">
                    Audience <span className="gcStudent-faq-required">*</span>
                  </label>
                  <div className="gcStudent-faq-selectWrap">
                    <select
  className="gcStudent-faq-select"
  value={form.audience}
  onChange={handleFormChange("audience")}
>
                     <option value="Student">Student</option>
<option value="Teacher">Teacher</option>

</select>

<span className="gcStudent-faq-selectArrow">
<ChevronDown size={16}/>
</span>

</div>

{errors.audience && (
<small className="gcStudent-error-text">
{errors.audience}
</small>
)}


                </div>

                <div className="gcStudent-faq-formGroup">
                  <label className="gcStudent-faq-formLabel">
                    Question <span className="gcStudent-faq-required">*</span>
                  </label>
                  <input
type="text"
className={`gcStudent-faq-input ${
errors.question ? "gcStudent-input-error" : ""
}`}
placeholder="e.g. How do I reserve a book?"
value={form.question}
onChange={handleFormChange("question")}
/>

{errors.question && (
<small className="gcStudent-error-text">
{errors.question}
</small>
)}
</div>

                <div className="gcStudent-faq-formGroup">
                  <label className="gcStudent-faq-formLabel">
                    Answer <span className="gcStudent-faq-required">*</span>
                  </label>
                  <textarea
className={`gcStudent-faq-textarea ${
errors.answer ? "gcStudent-input-error" : ""
}`}
rows={4}
placeholder="Provide a clear, helpful answer..."
value={form.answer}
onChange={handleFormChange("answer")}
/>

{errors.answer && (
<small className="gcStudent-error-text">
{errors.answer}
</small>
)}
                </div>
              </div>

              <div className="gcStudent-faq-modalFooter">
                <button type="submit" className="gcStudent-faq-submitBtn" disabled={submitting}>
                  {submitting
                    ? modalMode === "create"
                      ? "Creating..."
                      : "Updating..."
                    : modalMode === "create"
                    ? "Create FAQ"
                    : "Update FAQ"}
                </button>
                <button
                  type="button"
                  className="gcStudent-faq-cancelBtn"
                  onClick={() => closeModal()}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQManagementpage;